<template>
  <UModal
    :value="detailTariffDialog"
    class="detail-tariff"
    @input="$emit('closeDetail')"
  >
    <template #default>
      <div v-if="loading">
        <h3>{{ $t('tariffs.loadingTariff') }}</h3>
        <v-progress-linear :indeterminate="true" />
      </div>
      <div
        v-else
        class="ma-0 tariff-content"
      >
        <div class="header ma-0 mb-6">
          <div class="tariff-name">
            {{ tariff.name }}
            <v-chip
              v-if="tariff.uuid === myTariff && currentTariff && currentTariff.trial_period"
              small
              :color="$vuetify.theme.dark ? '#FFFFFF' : '#7F97AB'"
              :text-color="$vuetify.theme.dark ? 'rgba(0, 0, 0, 0.87)' : '#FFFFFF'"
            >
              {{ $t("tariffs.trial") }}
            </v-chip>
            <v-chip
              v-if="tariff.uuid === myTariff && currentTariff && !currentTariff.trial_period"
              small
              :color="$vuetify.theme.dark ? '#FFFFFF' : '#7F97AB'"
              :text-color="$vuetify.theme.dark ? 'rgba(0, 0, 0, 0.87)' : '#FFFFFF'"
            >
              {{ $t("tariffs.yourTariff") }}
            </v-chip>
          </div>
          <div class="transaction-fee">
            <span v-if="!isApplication">{{ tariff.transaction_fee }}% {{ $t('tariffs.fromTransactions') }}</span>
            <span
              v-else
              class="individually"
            >
              {{ $t('tariffs.individually') }}
            </span>
          </div>
        </div>
        <div
          v-if="!isApplication"
          class="ma-0 mb-6"
        >
          <v-btn-toggle
            v-model="togglePrice"
            variant="outlined"
          >
            <v-btn
              value="month"
            >
              <div class="d-flex price">
                <div class="d-flex price-month align-center mb-1">
                  <span class="price-month_count">{{ 'USD' | renderSymbol }}{{ tariff.price_monthly}}</span>
                  <span class="price-month_text">/{{ $t('tariffs.monthShort') }}.</span>
                </div>
                <div class="price-description">
                  {{ $t('tariffs.payingMonth') }}
                </div>
              </div>
            </v-btn>
            <v-btn
              value="year"
            >
              <div class="d-flex price">
                <div class="d-flex price-month align-center mb-1">
                  <span class="price-month_count-through">{{ 'USD' | renderSymbol }}{{ tariff.price_monthly}}</span>
                  <span class="price-month_count">{{ 'USD' | renderSymbol }}{{ getMothPrice }}</span>
                  <span class="price-month_text">/{{ $t('tariffs.monthShort') }}.</span>
                </div>
                <div class="price-description">
                  {{ $t('tariffs.payingYear') }}
                </div>
              </div>
            </v-btn>
          </v-btn-toggle>
        </div>
        <div
          v-else
          class="ma-0 mb-6 d-flex align-center justify-center individual-conditions"
        >
          {{ $t('tariffs.individualConditions') }}
        </div>
        <div
          v-if="modulesConfig.length !== 0"
          class="ma-0 mb-3 parameters"
        >
          <p class="ma-0 mb-3 parameters-name">
            {{ $t('tariffs.modules') }}:
          </p>
          <ul class="parameters-list">
            <li
              v-for="(item, indx) in modulesConfig"
              :key="item+indx"
            >
              {{ item }}
            </li>
          </ul>
        </div>
        <div
          v-if="affiliateConfig.length !== 0"
          class="ma-0 mb-3 parameters"
        >
          <p class="ma-0 mb-3 parameters-name">
            {{ $t('tariffs.marketing') }}:
          </p>
          <ul class="parameters-list">
            <li
              v-for="(item, indx) in affiliateConfig"
              :key="item+indx"
            >
              {{ item }}
            </li>
          </ul>
        </div>
        <div
          v-if="gatewaysConfig.length !== 0"
          class="ma-0 mb-3 parameters"
        >
          <p class="ma-0 mb-3 parameters-name">
            {{ $t('tariffs.paymentSystems') }}:
          </p>
          <ul class="parameters-list">
            <li
              v-for="(item, indx) in gatewaysConfig"
              :key="item+indx"
            >
              {{ item }}
            </li>
          </ul>
        </div>
        <div
          v-if="optionsConfig.length !== 0"
          class="ma-0 mb-3 parameters"
        >
          <p class="ma-0 mb-3 parameters-name">
            {{ $t('tariffs.additionally') }}:
          </p>
          <ul class="parameters-list">
            <li
              v-for="(item, indx) in optionsConfig"
              :key="item+indx"
            >
              {{ item }}
            </li>
          </ul>
        </div>
        <div
          v-if="generalTariff && generalTariff.storage"
          class="ma-0 mb-6 parameters"
        >
          <p class="ma-0 parameters-storage">
            {{ $t('tariffs.dataLimit') }} - {{ generalTariff.storage }}gb
          </p>
        </div>
        <v-list
          v-if="getDescription.length !== 0"
          class="ma-0 mb-6 tariff-description"
          flat
          color="transparent"
        >
          <v-list-item
            v-for="(item, i) in getDescription"
            :key="i"
            class="pa-0 mb-2"
          >
            <v-list-item-icon class="ma-0 mr-2">
              <v-icon>mdi-check</v-icon>
            </v-list-item-icon>
            <v-list-item-content class="pa-0">
              <v-list-item-title class="text-left text-wrap">
                {{ item }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <div
          v-if="generalTariff"
          class="ma-0 mb-6 parameters-general"
        >
          <v-list-item
            class="pa-0 ma-0 pb-3"
          >
            <v-list-item-title class="pa-0 d-flex justify-space-between align-center">
              <div class="general-title">{{ $t('tariffs.userLimit') }}</div>
              <div class="general-value">{{ generalTariff.number_of_users || 0 }}</div>
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            class="pa-0 ma-0 py-3"
          >
            <v-list-item-title class="pa-0 d-flex justify-space-between align-center">
              <div class="general-title">{{ $t('tariffs.dataLimit') }}</div>
              <div class="general-value">{{ generalTariff.storage || 0 }}Gb</div>
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            class="pa-0 ma-0 py-3"
          >
            <v-list-item-title class="pa-0 d-flex justify-space-between align-center">
              <div class="general-title">{{ $t('tariffs.companyLimit') }}</div>
              <div class="general-value">{{ generalTariff.number_of_whitelabels || 0 }}</div>
            </v-list-item-title>
          </v-list-item>
        </div>
      </div>
    </template>
    <template #fixedContent>
      <v-btn
        v-if="!loading && (!myTariff && generalTariff && generalTariff.trial_period && !isApplication)"
        class="primary tariff-buy mb-6"
        block
        @click="startTrial"
      >
        {{ $t('tariffs.startTrial') }}
      </v-btn>
      <v-btn
        v-else
        class="primary tariff-buy mb-6"
        block
        :disabled="tariff.uuid === myTariff && currentTariff.trial_period === false && currentTariff.status === 'active'"
        @click="buyOrSubmitTariff"
      >
        {{ isApplication ? $t('tariffs.submitApplication') : $t('tariffs.buy') }}
      </v-btn>
    </template>
  </UModal>
</template>

<script>
import PRICINGS_CONFIG_QUERY from "@/graphql/queries/ProviderPricingConfig.gql";


import { mapGetters } from 'vuex'
import UModal from "@/components/helper/UModal.vue";

export default {
  name: "DetailDialog",
  components: {
    UModal
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    tariff: {
      type: Object,
      default: null
    },
    period: {
      type: String,
      default: "year"
    }
  },
  data() {
    return {
      loading: true,
      togglePrice: "year",
      providerUuid: this.$ls.get('config').uuid,
      modulesConfig: [],
      affiliateConfig: [],
      gatewaysConfig: [],
      optionsConfig: [],
      generalTariff: null,
    }
  },
  computed: {
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    ...mapGetters("user", ["currentTariff"]),
    detailTariffDialog: {
      get() {
        return this.showDialog;
      },
      set(val) {
        this.$emit("closeDetail", val);
      }
    },
    getMothPrice() {
      return (this.tariff.price_annually/12).toFixed(0)
    },
    getDescription() {
      return this.tariff.description ? this.tariff.description.split(/\n/) : []
    },
    myTariff() {
      return this.currentTariff ? this.currentTariff.pricing.uuid : null
    },
    isApplication() {
      return this.generalTariff && this.generalTariff.individual_conditions && this.generalTariff.link_to_form.length > 0
    }
  },
  watch: {
    providerPricingConfig: {
      handler(val) {
        if(val) {
          this.getNameFromConfig(val)
        }
      }
    }
  },
  mounted() {
    this.togglePrice = this.period;
  },
  methods: {
    buyOrSubmitTariff() {
      if(this.isApplication) {
        this.$emit('submit-tariff', this.generalTariff.link_to_form);
      } else {
        this.$emit('buy-tariff', this.tariff, this.togglePrice);
      }
      this.detailTariffDialog = false;
    },
    startTrial() {
      this.$emit('trial', this.tariff.uuid, this.generalTariff.trial_period);
      this.detailTariffDialog = false;
    },
    async getNameFromConfig(configPricingSchema) {
      this.loading = true;
      const configTariff = JSON.parse(this.tariff.config);
      const pricingConfig = JSON.parse(configPricingSchema.schema);
      this.generalTariff = configTariff.general[0];

      if(configTariff.modules.length !== 0) {
        this.modulesConfig = await this.checkConfig(pricingConfig, configTariff, 'modules');
      }
      if(configTariff.affiliate_program.length !== 0) {
        this.affiliateConfig = await this.checkConfig(pricingConfig, configTariff, 'affiliate_program');
      }
      if(configTariff.gateways.length !== 0) {
        this.gatewaysConfig = await this.checkConfig(pricingConfig, configTariff, 'gateways');
      }
      if(configTariff.options.length !== 0) {
        this.optionsConfig = await this.checkConfig(pricingConfig, configTariff, 'options');
      }
      this.loading = false;
    },
    checkConfig(pricingConfig, configTariff, name){
      const nameArray = [];
      for(let item in configTariff[name][0]) {
        let findModule = pricingConfig[name].find(el => el.key === item);
        if(findModule) {
          if(name === 'gateways') {
            nameArray.push(findModule.name);
          } else {
            nameArray.push(findModule.name[this.$i18n.locale]);
          }
        }
      }
      return nameArray;
    }
  },
  apollo: {
    providerPricingConfig: {
      fetchPolicy: "network-only",
      query() {
        return  PRICINGS_CONFIG_QUERY;
      },
      variables() {
        return {
          provider_uuid: this.providerUuid
        }
      },
      skip() {
        return !this.providerUuid;
      },
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/material-dashboard/_colors.scss";
@import "@/styles/material-dashboard/_variables.scss";

.detail-tariff {
  
  .tariff-content {
    flex-direction: column;
    overflow-y: auto;
    max-height: calc(100vh - 158px);
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 7px;
    }

    &::-webkit-scrollbar-track {
      background-color: #F1F1F1 ;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #C1C1C1;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-button,
    &::-webkit-scrollbar-track-piece {
      display: none;
    }
    .header {
      flex-direction: column;
      .tariff-name {
        font-size: $font-size-25;
        font-style: normal;
        font-weight: $font-weight-600;
        max-width: 90%;
        margin-bottom: 4px;
      }
      .transaction-fee {
        font-size: $font-size-13;
        font-weight: $font-weight-bolder;
        .individually {
          text-transform: capitalize;
        }
      }
    }
    .v-item-group.v-btn-toggle {
      border-radius: 8px;
      background-color: transparent;
      border: 1px solid rgba(49, 57, 44, 0.10);
      .v-btn {
        width: 50%;
        height: auto;
        border-radius: 8px;
        padding: 8px 16px;
        border: none;
        text-transform: unset;
        font-size: $font-size-default;
        font-weight: $font-weight-600;
        background-color: transparent;
        &.v-btn--active {
          background-color: var(--primary-color, #0059FF) !important;
          color: $white;
        }
        .price {
          flex-direction: column;
          .price-month {
            .price-month_count {
              font-size: $font-size-toolbar-items;
              font-weight: $font-weight-600;
            }
            .price-month_text {
              opacity: .7;
              font-size: $font-size-11;
              font-weight: $font-weight-bolder;
            }
            .price-month_count-through {
              opacity: .7;
              font-size: $font-size-11;
              font-weight: $font-weight-bolder;
              text-decoration: line-through;
            }
          }
          .price-description {
            font-size: $font-size-11;
            font-weight: $font-weight-bolder;
          }
        }
      }
    }
    .individual-conditions {
      min-height: 57px;
      font-size: $font-size-13;
      font-style: normal;
      font-weight: $font-weight-bolder;
    }
    .parameters {
      .parameters-name {
        font-weight: $font-weight-600;
        font-size: $font-size-13 !important;
      }
      .parameters-list, .parameters-storage {
        font-weight: $font-weight-bolder;
        font-size: $font-size-13 !important;
      }
    }
    .tariff-description {
      .v-list-item {
        min-height: auto;
        .v-list-item__icon {
          min-width: auto;
          align-items: center;
          padding-top: 2px;
          .v-icon {
            color: var(--primary-color, #0059FF) !important;
            font-size: 12px;
          }
        }
        .v-list-item__content {
          .v-list-item__title {
            font-size: $font-size-13;
            font-style: normal;
            font-weight: $font-weight-bolder;
          }
        }
      }
    }
    .parameters-general {
      .v-list-item {
        min-height: auto;
        border-bottom: 1px dashed var(--light-theme-surface-color-10, rgba(49, 57, 44, 0.10));
        &:last-child {
          border-bottom: unset;
        }
        .v-list-item__title {
          .general-title {
            color: var(--secondary-color, #96A2AE);
            font-size: $font-size-11;
            font-weight: $font-weight-bolder;
          }
          .general-value {
            font-size: $font-size-13;
            font-weight: $font-weight-600;
          }
        }
      }
    }
  }
  .tariff-buy {
    height: 64px;
    padding: 16px 24px;
    border-radius: 8px;
    font-size: $font-size-17;
    font-weight: $font-weight-600;
    background: var(--primary-color, #0059FF);
  }
}
</style>
